import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError, api, setUnauthorizedHandler, tokenStore } from '@/api/client';
import type { Banner, LoginResponse, MeResponse, Pharmacy, User } from '@/api/types';
import { type PermissionKey, hasAny, hasPermission } from '@/auth/permissions';

interface SessionValue {
  user: User | null;
  pharmacy: Pharmacy | null;
  banner: Banner | null;
  /** True until the initial /auth/me settles — render a splash, not the login screen. */
  loading: boolean;
  isAuthenticated: boolean;
  isSuperadmin: boolean;
  /** Writes are blocked: expired, in grace, or suspended. */
  isReadOnly: boolean;
  can: (key: PermissionKey) => boolean;
  canAny: (keys: PermissionKey[]) => boolean;
  login: (email: string, password: string, pharmacySlug?: string) => Promise<LoginResponse>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const [user, setUser] = useState<User | null>(null);
  const [pharmacy, setPharmacy] = useState<Pharmacy | null>(null);
  const [banner, setBanner] = useState<Banner | null>(null);
  const [loading, setLoading] = useState(true);

  const clear = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setPharmacy(null);
    setBanner(null);
    // Cached tenant data must not survive a logout — the next person to sign in
    // at this till could be from a different account.
    queryClient.clear();
  }, [queryClient]);

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      clear();
      setLoading(false);
      return;
    }

    try {
      const me = await api.get<MeResponse>('/api/auth/me');
      setUser(me.user);
      setPharmacy(me.pharmacy ?? null);
      setBanner(me.banner ?? null);
    } catch (error) {
      // A 401 already cleared the token via the client's handler. Anything else
      // — a network blip, a cold-starting server — shouldn't sign the user out.
      if (error instanceof ApiError && error.isUnauthorized) clear();
    } finally {
      setLoading(false);
    }
  }, [clear]);

  // A 401 from any request drops the session, so the UI can't sit showing data
  // it is no longer allowed to refresh.
  useEffect(() => {
    setUnauthorizedHandler(clear);
    return () => setUnauthorizedHandler(null);
  }, [clear]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, pharmacySlug?: string) => {
      const result = await api.post<LoginResponse>('/api/auth/login', {
        email,
        password,
        pharmacySlug,
      });

      tokenStore.set(result.token);
      setUser(result.user);
      setPharmacy(result.pharmacy ?? null);
      // The banner comes from /auth/me, so fetch it rather than leaving a stale
      // renewal warning from a previous session on screen.
      void refresh();

      return result;
    },
    [refresh],
  );

  const value = useMemo<SessionValue>(() => {
    const isSuperadmin = user?.role === 'Superadmin';

    return {
      user,
      pharmacy,
      banner,
      loading,
      isAuthenticated: user !== null,
      isSuperadmin,
      isReadOnly:
        !isSuperadmin &&
        pharmacy !== null &&
        (!pharmacy.isActive || pharmacy.status === 'Suspended'),
      can: (key) => hasPermission(user, key),
      canAny: (keys) => hasAny(user, keys),
      login,
      logout: clear,
      refresh,
    };
  }, [user, pharmacy, banner, loading, login, clear, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// The hook and its provider are one cohesive unit; splitting them into two
// files for a fast-refresh nicety isn't worth the indirection.
// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>.');
  return context;
}
